import styled from '@emotion/styled';
import * as Sentry from '@sentry/react';
import { lazy, Suspense, useEffect } from 'react';
import {
    BrowserRouter,
    createRoutesFromChildren,
    matchRoutes,
    Route,
    Routes,
    useLocation,
    useNavigationType,
} from 'react-router';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ScrollToTop } from '@/components/ScrollToTop';
import globals from '@/lib/globals';
import { useCheckAuth } from '@/lib/hooks/useAuth';

import { BigSpinner } from './BigSpinner';
import { InviteTokenRoute } from './InviteTokenRoute';
import { PrivateRoute } from './PrivateRoute';
import { ResetTokenRoute } from './ResetTokenRoute';

const ContentRoot = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 100vh;
`;

const Content = styled.div`
    flex: 1 1 auto;
    flex-grow: 1;
    padding-left: 0px;
    padding-right: 0px;
`;

const ForgotPassword = lazy(() => import('../pages/ForgotPassword'));
const Login = lazy(() => import('../pages/Login'));
const DeepLink = lazy(() => import('../pages/DeepLink'));
const Main = lazy(() => import('../pages/Main'));
const NotFound = lazy(() => import('../pages/NotFound'));
const ResetPassword = lazy(() => import('../pages/Auth/ResetPassword'));
const ChangePassword = lazy(() => import('../pages/Auth/ChangePassword'));
const UserProfile = lazy(() => import('../pages/UserProfile'));
const UpdatePassword = lazy(() => import('../pages/Auth/UpdatePassword'));
const AcceptInvitePage = lazy(() => import('../pages/Auth/AcceptInvitePage'));

const CourseListPage = lazy(() => import('../pages/Courses/CourseList'));
const CourseDetailPage = lazy(() => import('../pages/Courses/CourseDetail'));
const CoursePlayer = lazy(() => import('../pages/Courses/CoursePlayer'));

// Org Admin
const Admin = lazy(() => import('../pages/Admin'));
const AdminUsersList = lazy(() => import('../pages/Admin/Users/UserList'));
const AdminUserDetail = lazy(() => import('../pages/Admin/Users/AdminUserDetail'));
const AdminInvitedUsers = lazy(() => import('../pages/Admin/InvitedUsers'));

// Platform Admin
const PlatformAdminOrgList = lazy(() => import('../pages/PlatformAdmin/Orgs/OrgList'));
const PlatformAdminOrgDetail = lazy(() => import('../pages/PlatformAdmin/Orgs/OrgDetail'));
const PlatformAdminOrgCreator = lazy(() => import('../pages/PlatformAdmin/Orgs/OrgCreator'));
const PlatformAdminUserList = lazy(() => import('../pages/PlatformAdmin/Users/UserAdminList'));
const PlatformAdminCourseList = lazy(() => import('../pages/PlatformAdmin/Courses/CourseAdminList'));
const CourseCreator = lazy(() => import('../pages/PlatformAdmin/Courses/CourseCreator'));
const PlatformAdminCourseDetail = lazy(() => import('../pages/PlatformAdmin/Courses/CourseDetail'));
const PlatformAdminEmailList = lazy(() => import('../pages/PlatformAdmin/Emails/EmailList'));
const PlatformAdminVideoDetail = lazy(() => import('../pages/PlatformAdmin/Videos/VideoDetail'));
const PlatformAdminVideoList = lazy(() => import('../pages/PlatformAdmin/Videos/VideoList'));
const UploadVideo = lazy(() => import('../pages/PlatformAdmin/Videos/UploadVideo'));
const PlatformAdmin = lazy(() => import('../pages/PlatformAdmin'));
const OauthLogin = lazy(() => import('../pages/OauthLogin'));
const Version = lazy(() => import('../pages/Version'));
const OauthAuthenticate = lazy(() => import('../pages/OauthAuthenticate'));
const RegisteredUsersReport = lazy(() => import('../pages/Reports/RegisteredUsers'));
const CourseCompletionsReport = lazy(() => import('../pages/Reports/CourseCompletions'));

Sentry.init({
    dsn: 'https://063269b76e6644108d87aa83f7816cf8@o4504028143026176.ingest.sentry.io/4504028144140288',
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.reactRouterBrowserTracingIntegration({
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
        }),
    ],

    tracesSampleRate: 0.1,
    normalizeDepth: 10,
    environment: globals.environment,
});

const SentryRoutes = Sentry.wrapReactRouterRouting(Routes);

export const AppRouter = () => {
    const { checkAuth } = useCheckAuth();

    // biome-ignore lint/correctness/useExhaustiveDependencies: one time effect
    useEffect(() => {
        checkAuth();
    }, []);

    return (
        <BrowserRouter>
            <ScrollToTop />
            <ContentRoot>
                <Content>
                    <ErrorBoundary key={location.pathname}>
                        <Suspense fallback={<BigSpinner />}>
                            <SentryRoutes>
                                <Route path="/login" element={<Login />} />
                                <Route path="/deep-link" element={<DeepLink />} />
                                <Route path="/oauth/login/:provider" element={<OauthLogin />} />
                                <Route path="/oauth/auth/:provider" element={<OauthAuthenticate />} />
                                <Route path="/forgot-password" element={<ForgotPassword />} />
                                <Route element={<ResetTokenRoute />}>
                                    <Route path="/reset-password/:token" element={<ResetPassword />} />
                                </Route>
                                <Route element={<InviteTokenRoute />}>
                                    <Route path="/accept-invite/:token" element={<AcceptInvitePage />} />
                                </Route>
                                <Route element={<PrivateRoute />}>
                                    <Route path="/" element={<Main />} />
                                    <Route path="/admin" element={<Admin />} />
                                    <Route path="/admin/members" element={<AdminUsersList />} />
                                    <Route path="/admin/members/invited" element={<AdminInvitedUsers />} />
                                    <Route path="/admin/members/:userId" element={<AdminUserDetail />} />
                                    <Route path="/change-password" element={<ChangePassword />} />
                                    <Route path="/profile" element={<UserProfile />} />
                                    <Route path="/courses" element={<CourseListPage />} />
                                    <Route path="/courses/:courseId" element={<CourseDetailPage />} />
                                    <Route path="/courses/player/:courseId" element={<CoursePlayer />} />
                                    <Route path="/platform-admin" element={<PlatformAdmin />} />
                                    <Route path="/platform-admin/orgs" element={<PlatformAdminOrgList />} />
                                    <Route path="/platform-admin/orgs/:orgId" element={<PlatformAdminOrgDetail />} />
                                    <Route
                                        path="/platform-admin/orgs/:orgId/:tab"
                                        element={<PlatformAdminOrgDetail />}
                                    />
                                    <Route path="/platform-admin/orgs/create" element={<PlatformAdminOrgCreator />} />
                                    <Route path="/platform-admin/users" element={<PlatformAdminUserList />} />
                                    <Route path="/platform-admin/courses" element={<PlatformAdminCourseList />} />
                                    <Route path="/platform-admin/courses/create" element={<CourseCreator />} />
                                    <Route
                                        path="/platform-admin/courses/:courseId"
                                        element={<PlatformAdminCourseDetail />}
                                    />
                                    <Route
                                        path="/platform-admin/courses/:courseId/edit"
                                        element={<PlatformAdminCourseDetail />}
                                    />
                                    <Route path="/platform-admin/emails" element={<PlatformAdminEmailList />} />
                                    <Route path="/platform-admin/videos" element={<PlatformAdminVideoList />} />
                                    <Route
                                        path="/platform-admin/videos/:videoId"
                                        element={<PlatformAdminVideoDetail />}
                                    />
                                    <Route path="/platform-admin/videos/upload" element={<UploadVideo />} />
                                    <Route path="/reports/registered-users" element={<RegisteredUsersReport />} />
                                    <Route path="/reports/course-completions" element={<CourseCompletionsReport />} />
                                    <Route path="/update-password" element={<UpdatePassword />} />
                                    <Route path="/version" element={<Version />} />
                                    <Route path="*" element={<NotFound />} />
                                </Route>
                            </SentryRoutes>
                        </Suspense>
                    </ErrorBoundary>
                </Content>
            </ContentRoot>
        </BrowserRouter>
    );
};

export default AppRouter;
